import React from "react";
import {
  Grid,
  TextField,
  Box,
  Typography,
  IconButton,
  FormControl,
  FormHelperText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";

export default function ExcelStepForm({
  data,
  updateData,
  setIsValid,
  showErrors,
}) {
  const inputRef = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const errors = {};

  if (!data.title) errors.title = "Nome é obrigatório";
  if (!data.desc) errors.desc = "Descrição é obrigatória";
  if (!data.file) errors.file = "Arquivo Excel é obrigatório";

  React.useEffect(() => {
    setIsValid(!!data.title && !!data.desc && !!data.file);
  }, [data.title, data.desc, data.file, setIsValid]);

  const handleChange = (name) => (e) => {
    updateData({ [name]: e.target.value });
  };

  /* =========================
     Validação mais segura
  ========================= */
  const handleFile = (file) => {
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    const isExcel = ["xlsx", "xls"].includes(extension);

    if (!isExcel) return;

    updateData({ file });
  };

  /* =========================
     Drag Events
  ========================= */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;

    const file = e.dataTransfer.files[0];
    handleFile(file);

    e.dataTransfer.clearData();
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  const handleClick = () => {
    if (!data.file) {
      inputRef.current?.click();
    }
  };

  const removeFile = () => {
    updateData({ file: null });
  };

  const fileHasError = showErrors && !!errors.file;

  return (
    <Box sx={{ width: "100%", maxWidth: 900 }}>
      <Grid container spacing={3}>
        {/* NOME */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Nome da listagem"
            value={data.title || ""}
            onChange={handleChange("title")}
            error={showErrors && !!errors.title}
            helperText={showErrors && errors.title}
          />
        </Grid>

        {/* DESCRIÇÃO */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Descrição"
            value={data.desc || ""}
            onChange={handleChange("desc")}
            multiline
            minRows={3}
            error={showErrors && !!errors.desc}
            helperText={showErrors && errors.desc}
          />
        </Grid>

        {/* DROPZONE */}
        <Grid item xs={12} md={8}>
          <FormControl fullWidth error={fileHasError}>
            <Box
              onClick={handleClick}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: "2px dashed",
                borderColor: fileHasError
                  ? "error.main"
                  : isDragging
                  ? "#7C3AED"
                  : "#C4B5FD",
                borderRadius: "12px",
                padding: "40px 20px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragging
                  ? "#F3F0FF"
                  : "#FAF9FF",
                transition: "0.2s ease",
              }}
            >
              {!data.file ? (
                <>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: fileHasError
                        ? "error.main"
                        : "#7C3AED",
                      mb: 1,
                    }}
                  />
                  <Typography sx={{ fontWeight: 500 }}>
                    Arraste seu arquivo Excel aqui
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748B", mt: 1 }}
                  >
                    ou clique para selecionar (.xlsx ou .xls)
                  </Typography>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#FFFFFF",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <InsertDriveFileIcon
                      sx={{
                        color: fileHasError
                          ? "error.main"
                          : "#7C3AED",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {data.file.name}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile();
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
            </Box>

            <input
              ref={inputRef}
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={handleFileInput}
            />

            {fileHasError && (
              <FormHelperText>
                {errors.file}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}